import {
  defaultQuantity,
  macroSplit,
  macrosFor,
  nutrientProfilePer100g,
  portionWeightG,
  previewGrams,
  scalePreview,
  sumMacros,
  type PassioFoodNutrient,
  type PassioMeasuredPortion,
} from "@passio/api-client";

/** Runnable client-side examples (no API calls) built from a real or example food. */

export type CodeLang = "ts" | "python" | "swift" | "kotlin";
export type CodeTopic = "portion" | "recipe" | "preview" | "totals";

export const CODE_LANGS: { value: CodeLang; label: string }[] = [
  { value: "ts", label: "TypeScript" },
  { value: "python", label: "Python" },
  { value: "swift", label: "Swift" },
  { value: "kotlin", label: "Kotlin" },
];

export const CODE_TOPICS: { value: CodeTopic; label: string; blurb: string }[] = [
  { value: "portion", label: "Nutrients for a portion", blurb: "Full-detail nutrients are per 100 g. Scale by the portion's grams and the serving count the user picked." },
  { value: "recipe", label: "Recipes", blurb: "Several ingredients, each with its own default portion: combine into one per-100 g profile, then scale to the dish's portion." },
  { value: "preview", label: "Search previews", blurb: "nutritionPreview on search, scan and extraction results is already scaled. Show it as-is; rescale it when the grams change." },
  { value: "totals", label: "Meal totals & macro split", blurb: "Add items into a meal and meals into a day, then work out the share of calories from protein, carbs and fat." },
];

export interface SampleNutrient {
  name: string;
  shortName: string;
  unit: string;
  /** Per 100 g. */
  amount: number;
}
export interface SamplePortion {
  name: string;
  quantity: number;
  suggestedQuantity: number;
  unit: string;
  value: number;
}
export interface SampleFood {
  name: string;
  nutrients: SampleNutrient[];
  portions: SamplePortion[];
}
export interface SampleMacros {
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}
export interface CodeSample {
  food: SampleFood;
  portionIndex: number;
  quantity: number;
  recipe: { name: string; ingredients: SampleFood[]; portion: SamplePortion; quantity: number };
  preview: { name: string; portion: Omit<SamplePortion, "suggestedQuantity">; calories: number; protein: number; carbs: number; fat: number; grams: number };
  meal: SampleMacros[];
}

const MACRO_ALIASES = {
  calories: ["calories", "energy"],
  protein: ["protein"],
  carbs: ["carbs", "carbohydrate", "carbohydrates", "carbohydrate, by difference"],
  fat: ["fat", "total fat", "total lipid (fat)"],
};

/** Strips characters that would need escaping differently in each language. */
const clean = (s: string) => s.replace(/["\\$`]/g, "").trim() || "Food";
/** Double literal valid in all four languages. */
const d = (n: number) => {
  const s = String(+(Number(n) || 0).toFixed(4));
  return /[.e]/.test(s) ? s : `${s}.0`;
};

export function toApiPortion(p: SamplePortion | SampleCodePreviewPortion): PassioMeasuredPortion {
  const sq = "suggestedQuantity" in p ? [p.suggestedQuantity] : undefined;
  return { name: p.name, quantity: p.quantity, suggestedQuantity: sq, weight: { unit: p.unit, value: p.value } };
}
type SampleCodePreviewPortion = CodeSample["preview"]["portion"];

export function toApiNutrients(food: SampleFood): PassioFoodNutrient[] {
  return food.nutrients.map((n, id) => ({ id, amount: n.amount, nutrient: { name: n.name, shortName: n.shortName, unit: n.unit } }));
}

function foodJson(food: SampleFood, indent = 2) {
  return JSON.stringify(
    { name: clean(food.name), nutrients: toApiNutrients(food).map(({ amount, nutrient }) => ({ amount, nutrient })), portions: food.portions.map(toApiPortion) },
    null,
    indent,
  );
}

/** What each example prints, computed with the demo's own helpers (the snippets are tested against this). */
export function expectedOutput(topic: CodeTopic, s: CodeSample): Record<string, number | string> {
  if (topic === "portion") {
    const portion = toApiPortion(s.food.portions[s.portionIndex]);
    const grams = portionWeightG(portion) * s.quantity;
    const m = macrosFor(nutrientProfilePer100g([{ nutrients: toApiNutrients(s.food) }]), grams);
    return { grams, calories: m.calories, protein: m.protein, carbs: m.carbs, fat: m.fat };
  }
  if (topic === "recipe") {
    const foods = s.recipe.ingredients.map((f) => ({ nutrients: toApiNutrients(f), portions: f.portions.map(toApiPortion) }));
    const recipeGrams = foods.reduce((g, f) => g + portionWeightG(f.portions[0]) * defaultQuantity(f.portions[0]), 0);
    const grams = portionWeightG(toApiPortion(s.recipe.portion)) * s.recipe.quantity;
    const m = macrosFor(nutrientProfilePer100g(foods), grams);
    return { recipeGrams, grams, calories: m.calories, protein: m.protein, carbs: m.carbs, fat: m.fat };
  }
  if (topic === "preview") {
    const preview = { ...s.preview, portion: toApiPortion(s.preview.portion) };
    const m = scalePreview(preview, s.preview.grams);
    return { previewGrams: previewGrams(preview), grams: s.preview.grams, calories: m.calories, protein: m.protein, carbs: m.carbs, fat: m.fat };
  }
  const t = sumMacros(s.meal.map(roundItem));
  const split = macroSplit(t);
  return { calories: t.calories, protein: t.protein, carbs: t.carbs, fat: t.fat, split: `${split.protein}/${split.carbs}/${split.fat}` };
}

/** `key=value` line in the format every snippet prints. */
export function outputLine(values: Record<string, number | string>): string {
  return Object.entries(values)
    .map(([k, v]) => `${k}=${typeof v === "number" ? v.toFixed(1) : v}`)
    .join(" ");
}

// ---------- TypeScript ----------

const TS_BASE = `interface Portion { name?: string; quantity?: number; suggestedQuantity?: number[]; weight?: { unit?: string; value?: number } }
interface FoodNutrient { amount?: number; nutrient?: { name?: string; shortName?: string; unit?: string } }
interface Food { name?: string; nutrients?: FoodNutrient[]; portions?: Portion[] }

const UNIT_TO_G: Record<string, number> = { g: 1, ml: 1, kg: 1000, mg: 0.001, oz: 28.3495, lb: 453.592 };

/** Grams in ONE of this portion (100 g when it has no weight). */
function portionGrams(p?: Portion): number {
  const value = Number(p?.weight?.value);
  if (!(value > 0)) return 100;
  return value * (UNIT_TO_G[(p?.weight?.unit || "g").toLowerCase()] ?? 1);
}

/** Default serving count: suggestedQuantity[0], else 1. portion.quantity is the unit size, not a count. */
function defaultQuantity(p?: Portion): number {
  const q = Number(p?.suggestedQuantity?.[0]);
  return q > 0 ? q : 1;
}

/** A nutrient's amount per 100 g, matched by shortName or name (0 when missing). */
function nutrientPer100g(nutrients: FoodNutrient[] = [], aliases: string[], unit?: string): number {
  const hit = nutrients.find((row) => {
    const n = row.nutrient ?? {};
    const named = aliases.includes((n.shortName ?? "").toLowerCase()) || aliases.includes((n.name ?? "").toLowerCase());
    return named && (!unit || (n.unit ?? "").toLowerCase() === unit);
  });
  return Number(hit?.amount) || 0;
}

/** N = n × (grams / 100) × quantity */
function forPortion(amountPer100g: number, p: Portion | undefined, quantity = defaultQuantity(p)): number {
  return amountPer100g * (portionGrams(p) / 100) * quantity;
}

type Macro = "calories" | "protein" | "carbs" | "fat";
const MACROS: Record<Macro, { aliases: string[]; unit?: string }> = {
  calories: { aliases: ${JSON.stringify(MACRO_ALIASES.calories)}, unit: "kcal" },
  protein: { aliases: ${JSON.stringify(MACRO_ALIASES.protein)} },
  carbs: { aliases: ${JSON.stringify(MACRO_ALIASES.carbs)} },
  fat: { aliases: ${JSON.stringify(MACRO_ALIASES.fat)} },
};
const f1 = (x: number) => x.toFixed(1);`;

function tsSnippet(topic: CodeTopic, s: CodeSample): string {
  if (topic === "portion") {
    return `// After GET /v2/products/napi/food/search/result/refCode/{refCode}:
// full nutrients live on results[0].ingredients[i]. Amounts are per 100 g.
${TS_BASE}

// ${clean(s.food.name)}, trimmed to the fields used here
const food: Food = ${foodJson(s.food)};

const portion = food.portions?.[${s.portionIndex}];
const quantity = ${d(s.quantity)}; // what the user picked; defaults to defaultQuantity(portion)
const grams = portionGrams(portion) * quantity;
const per100 = (k: Macro) => nutrientPer100g(food.nutrients, MACROS[k].aliases, MACROS[k].unit);

console.log(\`grams=\${f1(grams)} calories=\${f1(forPortion(per100("calories"), portion, quantity))} protein=\${f1(forPortion(per100("protein"), portion, quantity))} carbs=\${f1(forPortion(per100("carbs"), portion, quantity))} fat=\${f1(forPortion(per100("fat"), portion, quantity))}\`);
`;
  }
  if (topic === "recipe") {
    return `// A recipe's refCode returns several ingredients, each with its own nutrients and portions.
${TS_BASE}

const ingredients: Food[] = ${JSON.stringify(
      s.recipe.ingredients.map((f) => JSON.parse(foodJson(f, 0))),
      null,
      2,
    )};
const recipePortion: Portion = ${JSON.stringify(toApiPortion(s.recipe.portion))};
const quantity = ${d(s.recipe.quantity)};

// 1. Weigh each ingredient at its default portion and add up the nutrients.
let recipeGrams = 0;
const totals: Record<Macro, number> = { calories: 0, protein: 0, carbs: 0, fat: 0 };
for (const ing of ingredients) {
  const p = ing.portions?.[0];
  const g = portionGrams(p) * defaultQuantity(p);
  recipeGrams += g;
  for (const k of Object.keys(totals) as Macro[]) {
    totals[k] += (nutrientPer100g(ing.nutrients, MACROS[k].aliases, MACROS[k].unit) * g) / 100;
  }
}
// 2. Turn that into a per-100 g profile for the whole dish, then scale to the dish's portion.
const grams = portionGrams(recipePortion) * quantity;
const scaled = (k: Macro) => forPortion((totals[k] / recipeGrams) * 100, recipePortion, quantity);

console.log(\`recipeGrams=\${f1(recipeGrams)} grams=\${f1(grams)} calories=\${f1(scaled("calories"))} protein=\${f1(scaled("protein"))} carbs=\${f1(scaled("carbs"))} fat=\${f1(scaled("fat"))}\`);
`;
  }
  if (topic === "preview") {
    return `// Search / scan / extraction results carry nutritionPreview, already scaled to its portion.
// Show it as-is in lists. Don't apply the per-100 g formula to it.
interface Preview { portion?: { name?: string; quantity?: number; weight?: { unit?: string; value?: number } }; calories?: number; protein?: number; carbs?: number; fat?: number }

const UNIT_TO_G: Record<string, number> = { g: 1, ml: 1, kg: 1000, mg: 0.001, oz: 28.3495, lb: 453.592 };

/** Grams the preview describes. Here portion.quantity IS a count (3 × 52 g eggs). */
function previewGrams(p: Preview): number {
  const value = Number(p.portion?.weight?.value);
  if (!(value > 0)) return 0;
  const q = Number(p.portion?.quantity);
  return value * (UNIT_TO_G[(p.portion?.weight?.unit || "g").toLowerCase()] ?? 1) * (q > 0 ? q : 1);
}

/** Same food, different weight: e.g. the grams a scan predicted, or the user's edit. */
function rescale(p: Preview, grams: number) {
  const f = previewGrams(p) ? grams / previewGrams(p) : 0;
  return { calories: (p.calories ?? 0) * f, protein: (p.protein ?? 0) * f, carbs: (p.carbs ?? 0) * f, fat: (p.fat ?? 0) * f };
}

// ${clean(s.preview.name)}
const preview: Preview = ${JSON.stringify({ portion: toApiPortion(s.preview.portion), calories: s.preview.calories, protein: s.preview.protein, carbs: s.preview.carbs, fat: s.preview.fat }, null, 2)};
const grams = ${d(s.preview.grams)};
const m = rescale(preview, grams);
const f1 = (x: number) => x.toFixed(1);

console.log(\`previewGrams=\${f1(previewGrams(preview))} grams=\${f1(grams)} calories=\${f1(m.calories)} protein=\${f1(m.protein)} carbs=\${f1(m.carbs)} fat=\${f1(m.fat)}\`);
`;
  }
  return `// Each item is already scaled to what was eaten (preview as-is, or the portion math).
interface Macros { calories: number; protein: number; carbs: number; fat: number }

const meal: (Macros & { name: string })[] = ${JSON.stringify(s.meal.map(roundItem), null, 2)};

/** Meal = sum of its items. A day = sum of its meals (never re-add a total you were sent). */
function sum(items: Macros[]): Macros {
  return items.reduce((t, i) => ({ calories: t.calories + i.calories, protein: t.protein + i.protein, carbs: t.carbs + i.carbs, fat: t.fat + i.fat }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
}

/** % of calories from protein / carbs / fat (4, 4 and 9 kcal per gram). */
function macroSplit(t: Macros) {
  const p = t.protein * 4, c = t.carbs * 4, f = t.fat * 9;
  const total = p + c + f || 1;
  return [p, c, f].map((x) => Math.round((x / total) * 100));
}

const total = sum(meal);
const f1 = (x: number) => x.toFixed(1);
console.log(\`calories=\${f1(total.calories)} protein=\${f1(total.protein)} carbs=\${f1(total.carbs)} fat=\${f1(total.fat)} split=\${macroSplit(total).join("/")}\`);
`;
}

const roundItem = (m: SampleMacros) => ({ name: clean(m.name), calories: +m.calories.toFixed(2), protein: +m.protein.toFixed(2), carbs: +m.carbs.toFixed(2), fat: +m.fat.toFixed(2) });

// ---------- Python ----------

const PY_BASE = `import json

UNIT_TO_G = {"g": 1, "ml": 1, "kg": 1000, "mg": 0.001, "oz": 28.3495, "lb": 453.592}
MACROS = {
    "calories": (${pyList(MACRO_ALIASES.calories)}, "kcal"),
    "protein": (${pyList(MACRO_ALIASES.protein)}, None),
    "carbs": (${pyList(MACRO_ALIASES.carbs)}, None),
    "fat": (${pyList(MACRO_ALIASES.fat)}, None),
}


def portion_grams(portion):
    """Grams in ONE of this portion (100 g when it has no weight)."""
    weight = (portion or {}).get("weight") or {}
    value = float(weight.get("value") or 0)
    if value <= 0:
        return 100.0
    return value * UNIT_TO_G.get(str(weight.get("unit") or "g").lower(), 1)


def default_quantity(portion):
    """suggestedQuantity[0], else 1. portion["quantity"] is the unit size, not a count."""
    suggested = (portion or {}).get("suggestedQuantity") or []
    q = float(suggested[0]) if suggested else 1.0
    return q if q > 0 else 1.0


def nutrient_per_100g(nutrients, aliases, unit=None):
    """A nutrient's amount per 100 g, matched by shortName or name (0 when missing)."""
    for row in nutrients or []:
        n = row.get("nutrient") or {}
        names = {str(n.get("shortName") or "").lower(), str(n.get("name") or "").lower()}
        if names & set(aliases) and (unit is None or str(n.get("unit") or "").lower() == unit):
            return float(row.get("amount") or 0)
    return 0.0


def for_portion(amount_per_100g, portion, quantity=None):
    """N = n * (grams / 100) * quantity"""
    q = default_quantity(portion) if quantity is None else quantity
    return amount_per_100g * portion_grams(portion) / 100 * q`;

function pyList(xs: string[]) {
  return `[${xs.map((x) => JSON.stringify(x)).join(", ")}]`;
}

function pySnippet(topic: CodeTopic, s: CodeSample): string {
  if (topic === "portion") {
    return `# After GET /v2/products/napi/food/search/result/refCode/{refCode}:
# full nutrients live on results[0]["ingredients"][i]. Amounts are per 100 g.
${PY_BASE}


# ${clean(s.food.name)}, trimmed to the fields used here
food = json.loads(r"""${foodJson(s.food)}""")

portion = food["portions"][${s.portionIndex}]
quantity = ${d(s.quantity)}  # what the user picked; defaults to default_quantity(portion)
grams = portion_grams(portion) * quantity
m = {k: for_portion(nutrient_per_100g(food["nutrients"], aliases, unit), portion, quantity) for k, (aliases, unit) in MACROS.items()}

print(f"grams={grams:.1f} calories={m['calories']:.1f} protein={m['protein']:.1f} carbs={m['carbs']:.1f} fat={m['fat']:.1f}")
`;
  }
  if (topic === "recipe") {
    return `# A recipe's refCode returns several ingredients, each with its own nutrients and portions.
${PY_BASE}


ingredients = json.loads(r"""${JSON.stringify(
      s.recipe.ingredients.map((f) => JSON.parse(foodJson(f, 0))),
      null,
      2,
    )}""")
recipe_portion = json.loads(r"""${JSON.stringify(toApiPortion(s.recipe.portion))}""")
quantity = ${d(s.recipe.quantity)}

# 1. Weigh each ingredient at its default portion and add up the nutrients.
recipe_grams = 0.0
totals = {k: 0.0 for k in MACROS}
for ing in ingredients:
    p = (ing.get("portions") or [None])[0]
    g = portion_grams(p) * default_quantity(p)
    recipe_grams += g
    for k, (aliases, unit) in MACROS.items():
        totals[k] += nutrient_per_100g(ing.get("nutrients"), aliases, unit) * g / 100

# 2. Per-100 g profile for the whole dish, then scale to the dish's portion.
grams = portion_grams(recipe_portion) * quantity
m = {k: for_portion(v / recipe_grams * 100, recipe_portion, quantity) for k, v in totals.items()}

print(f"recipeGrams={recipe_grams:.1f} grams={grams:.1f} calories={m['calories']:.1f} protein={m['protein']:.1f} carbs={m['carbs']:.1f} fat={m['fat']:.1f}")
`;
  }
  if (topic === "preview") {
    return `# Search / scan / extraction results carry nutritionPreview, already scaled to its portion.
# Show it as-is in lists. Don't apply the per-100 g formula to it.
import json

UNIT_TO_G = {"g": 1, "ml": 1, "kg": 1000, "mg": 0.001, "oz": 28.3495, "lb": 453.592}


def preview_grams(preview):
    """Grams the preview describes. Here portion["quantity"] IS a count (3 x 52 g eggs)."""
    portion = preview.get("portion") or {}
    weight = portion.get("weight") or {}
    value = float(weight.get("value") or 0)
    if value <= 0:
        return 0.0
    q = float(portion.get("quantity") or 0)
    return value * UNIT_TO_G.get(str(weight.get("unit") or "g").lower(), 1) * (q if q > 0 else 1)


def rescale(preview, grams):
    """Same food, different weight: e.g. the grams a scan predicted, or the user's edit."""
    base = preview_grams(preview)
    f = grams / base if base else 0
    return {k: float(preview.get(k) or 0) * f for k in ("calories", "protein", "carbs", "fat")}


# ${clean(s.preview.name)}
preview = json.loads(r"""${JSON.stringify({ portion: toApiPortion(s.preview.portion), calories: s.preview.calories, protein: s.preview.protein, carbs: s.preview.carbs, fat: s.preview.fat }, null, 2)}""")
grams = ${d(s.preview.grams)}
m = rescale(preview, grams)

print(f"previewGrams={preview_grams(preview):.1f} grams={grams:.1f} calories={m['calories']:.1f} protein={m['protein']:.1f} carbs={m['carbs']:.1f} fat={m['fat']:.1f}")
`;
  }
  return `# Each item is already scaled to what was eaten (preview as-is, or the portion math).
import json

meal = json.loads(r"""${JSON.stringify(s.meal.map(roundItem), null, 2)}""")


def total(items):
    """Meal = sum of its items. A day = sum of its meals (never re-add a total you were sent)."""
    return {k: sum(float(i.get(k) or 0) for i in items) for k in ("calories", "protein", "carbs", "fat")}


def macro_split(t):
    """% of calories from protein / carbs / fat (4, 4 and 9 kcal per gram)."""
    p, c, f = t["protein"] * 4, t["carbs"] * 4, t["fat"] * 9
    whole = (p + c + f) or 1
    return [int((x / whole * 100) + 0.5) for x in (p, c, f)]


t = total(meal)
split = "/".join(str(x) for x in macro_split(t))
print(f"calories={t['calories']:.1f} protein={t['protein']:.1f} carbs={t['carbs']:.1f} fat={t['fat']:.1f} split={split}")
`;
}

// ---------- Swift ----------

const SWIFT_BASE = `import Foundation

// Decodable models matching the API's JSON keys
struct Weight: Decodable { let unit: String?; let value: Double? }
struct Portion: Decodable { let name: String?; let quantity: Double?; let suggestedQuantity: [Double]?; let weight: Weight? }
struct NutrientInfo: Decodable { let name: String?; let shortName: String?; let unit: String? }
struct FoodNutrient: Decodable { let amount: Double?; let nutrient: NutrientInfo? }
struct Food: Decodable { let name: String?; let nutrients: [FoodNutrient]?; let portions: [Portion]? }

let unitToGrams: [String: Double] = ["g": 1, "ml": 1, "kg": 1000, "mg": 0.001, "oz": 28.3495, "lb": 453.592]
let macros: [(key: String, aliases: [String], unit: String?)] = [
    ("calories", ${JSON.stringify(MACRO_ALIASES.calories)}, "kcal"),
    ("protein", ${JSON.stringify(MACRO_ALIASES.protein)}, nil),
    ("carbs", ${JSON.stringify(MACRO_ALIASES.carbs)}, nil),
    ("fat", ${JSON.stringify(MACRO_ALIASES.fat)}, nil),
]

/// Grams in ONE of this portion (100 g when it has no weight).
func portionGrams(_ p: Portion?) -> Double {
    guard let w = p?.weight, let value = w.value, value > 0 else { return 100 }
    return value * (unitToGrams[(w.unit ?? "g").lowercased()] ?? 1)
}

/// suggestedQuantity[0], else 1. portion.quantity is the unit size, not a count.
func defaultQuantity(_ p: Portion?) -> Double {
    if let q = p?.suggestedQuantity?.first, q > 0 { return q }
    return 1
}

/// A nutrient's amount per 100 g, matched by shortName or name (0 when missing).
func nutrientPer100g(_ nutrients: [FoodNutrient]?, _ aliases: [String], unit: String? = nil) -> Double {
    let hit = (nutrients ?? []).first(where: { row in
        let short = (row.nutrient?.shortName ?? "").lowercased()
        let name = (row.nutrient?.name ?? "").lowercased()
        let unitOk = unit == nil || (row.nutrient?.unit ?? "").lowercased() == unit
        return (aliases.contains(short) || aliases.contains(name)) && unitOk
    })
    return hit?.amount ?? 0
}

/// N = n × (grams / 100) × quantity
func forPortion(_ per100g: Double, _ p: Portion?, _ quantity: Double) -> Double {
    return per100g * portionGrams(p) / 100 * quantity
}

func f1(_ x: Double) -> String { String(format: "%.1f", x) }`;

function swiftSnippet(topic: CodeTopic, s: CodeSample): string {
  if (topic === "portion") {
    return `// After GET /v2/products/napi/food/search/result/refCode/{refCode}:
// full nutrients live on results[0].ingredients[i]. Amounts are per 100 g.
${SWIFT_BASE}

// ${clean(s.food.name)}, trimmed to the fields used here
let json = #"""
${foodJson(s.food)}
"""#
let food = try! JSONDecoder().decode(Food.self, from: Data(json.utf8))

let portion = food.portions?[${s.portionIndex}]
let quantity = ${d(s.quantity)} // what the user picked; defaults to defaultQuantity(portion)
let grams = portionGrams(portion) * quantity
var m: [String: Double] = [:]
for macro in macros {
    m[macro.key] = forPortion(nutrientPer100g(food.nutrients, macro.aliases, unit: macro.unit), portion, quantity)
}

print("grams=\\(f1(grams)) calories=\\(f1(m["calories"]!)) protein=\\(f1(m["protein"]!)) carbs=\\(f1(m["carbs"]!)) fat=\\(f1(m["fat"]!))")
`;
  }
  if (topic === "recipe") {
    return `// A recipe's refCode returns several ingredients, each with its own nutrients and portions.
${SWIFT_BASE}

let json = #"""
${JSON.stringify(
      s.recipe.ingredients.map((f) => JSON.parse(foodJson(f, 0))),
      null,
      2,
    )}
"""#
let ingredients = try! JSONDecoder().decode([Food].self, from: Data(json.utf8))
let recipePortion = try! JSONDecoder().decode(Portion.self, from: Data(#"${JSON.stringify(toApiPortion(s.recipe.portion))}"#.utf8))
let quantity = ${d(s.recipe.quantity)}

// 1. Weigh each ingredient at its default portion and add up the nutrients.
var recipeGrams = 0.0
var totals: [String: Double] = [:]
for ing in ingredients {
    let p = ing.portions?.first
    let g = portionGrams(p) * defaultQuantity(p)
    recipeGrams += g
    for macro in macros {
        totals[macro.key, default: 0] += nutrientPer100g(ing.nutrients, macro.aliases, unit: macro.unit) * g / 100
    }
}

// 2. Per-100 g profile for the whole dish, then scale to the dish's portion.
let grams = portionGrams(recipePortion) * quantity
func scaled(_ key: String) -> Double { forPortion((totals[key] ?? 0) / recipeGrams * 100, recipePortion, quantity) }

print("recipeGrams=\\(f1(recipeGrams)) grams=\\(f1(grams)) calories=\\(f1(scaled("calories"))) protein=\\(f1(scaled("protein"))) carbs=\\(f1(scaled("carbs"))) fat=\\(f1(scaled("fat")))")
`;
  }
  if (topic === "preview") {
    return `// Search / scan / extraction results carry nutritionPreview, already scaled to its portion.
// Show it as-is in lists. Don't apply the per-100 g formula to it.
import Foundation

struct Weight: Decodable { let unit: String?; let value: Double? }
struct PreviewPortion: Decodable { let name: String?; let quantity: Double?; let weight: Weight? }
struct NutritionPreview: Decodable { let portion: PreviewPortion?; let calories: Double?; let protein: Double?; let carbs: Double?; let fat: Double? }

let unitToGrams: [String: Double] = ["g": 1, "ml": 1, "kg": 1000, "mg": 0.001, "oz": 28.3495, "lb": 453.592]

/// Grams the preview describes. Here portion.quantity IS a count (3 × 52 g eggs).
func previewGrams(_ p: NutritionPreview) -> Double {
    guard let w = p.portion?.weight, let value = w.value, value > 0 else { return 0 }
    let q = p.portion?.quantity ?? 0
    return value * (unitToGrams[(w.unit ?? "g").lowercased()] ?? 1) * (q > 0 ? q : 1)
}

/// Same food, different weight: e.g. the grams a scan predicted, or the user's edit.
func rescale(_ p: NutritionPreview, grams: Double) -> (calories: Double, protein: Double, carbs: Double, fat: Double) {
    let base = previewGrams(p)
    let f = base > 0 ? grams / base : 0
    return ((p.calories ?? 0) * f, (p.protein ?? 0) * f, (p.carbs ?? 0) * f, (p.fat ?? 0) * f)
}

func f1(_ x: Double) -> String { String(format: "%.1f", x) }

// ${clean(s.preview.name)}
let json = #"""
${JSON.stringify({ portion: toApiPortion(s.preview.portion), calories: s.preview.calories, protein: s.preview.protein, carbs: s.preview.carbs, fat: s.preview.fat }, null, 2)}
"""#
let preview = try! JSONDecoder().decode(NutritionPreview.self, from: Data(json.utf8))
let grams = ${d(s.preview.grams)}
let m = rescale(preview, grams: grams)

print("previewGrams=\\(f1(previewGrams(preview))) grams=\\(f1(grams)) calories=\\(f1(m.calories)) protein=\\(f1(m.protein)) carbs=\\(f1(m.carbs)) fat=\\(f1(m.fat))")
`;
  }
  return `// Each item is already scaled to what was eaten (preview as-is, or the portion math).
import Foundation

struct Macros { var calories = 0.0, protein = 0.0, carbs = 0.0, fat = 0.0 }

let meal: [(name: String, macros: Macros)] = [
${s.meal
  .map(roundItem)
  .map((m) => `    ("${m.name}", Macros(calories: ${d(m.calories)}, protein: ${d(m.protein)}, carbs: ${d(m.carbs)}, fat: ${d(m.fat)})),`)
  .join("\n")}
]

/// Meal = sum of its items. A day = sum of its meals (never re-add a total you were sent).
func sum(_ items: [Macros]) -> Macros {
    items.reduce(into: Macros()) { t, i in
        t.calories += i.calories; t.protein += i.protein; t.carbs += i.carbs; t.fat += i.fat
    }
}

/// % of calories from protein / carbs / fat (4, 4 and 9 kcal per gram).
func macroSplit(_ t: Macros) -> [Int] {
    let parts = [t.protein * 4, t.carbs * 4, t.fat * 9]
    let whole = parts.reduce(0, +)
    return parts.map { Int(($0 / (whole > 0 ? whole : 1) * 100).rounded()) }
}

func f1(_ x: Double) -> String { String(format: "%.1f", x) }

let total = sum(meal.map { $0.macros })
let split = macroSplit(total).map { String($0) }.joined(separator: "/")
print("calories=\\(f1(total.calories)) protein=\\(f1(total.protein)) carbs=\\(f1(total.carbs)) fat=\\(f1(total.fat)) split=\\(split)")
`;
}

// ---------- Kotlin ----------

const KT_MODELS = `import java.util.Locale

// Same field names as the API JSON: map them with kotlinx.serialization, Moshi or Gson.
data class Weight(val unit: String?, val value: Double?)
data class Portion(val name: String?, val quantity: Double?, val suggestedQuantity: List<Double>?, val weight: Weight?)
data class NutrientInfo(val name: String?, val shortName: String?, val unit: String?)
data class FoodNutrient(val amount: Double?, val nutrient: NutrientInfo?)
data class Food(val name: String?, val nutrients: List<FoodNutrient>?, val portions: List<Portion>?)

val unitToGrams = mapOf("g" to 1.0, "ml" to 1.0, "kg" to 1000.0, "mg" to 0.001, "oz" to 28.3495, "lb" to 453.592)
val macros = listOf(
    Triple("calories", ${ktList(MACRO_ALIASES.calories)}, "kcal"),
    Triple("protein", ${ktList(MACRO_ALIASES.protein)}, null),
    Triple("carbs", ${ktList(MACRO_ALIASES.carbs)}, null),
    Triple("fat", ${ktList(MACRO_ALIASES.fat)}, null),
)

/** Grams in ONE of this portion (100 g when it has no weight). */
fun portionGrams(p: Portion?): Double {
    val w = p?.weight ?: return 100.0
    val value = w.value ?: return 100.0
    if (value <= 0) return 100.0
    return value * (unitToGrams[(w.unit ?: "g").lowercase()] ?: 1.0)
}

/** suggestedQuantity[0], else 1. portion.quantity is the unit size, not a count. */
fun defaultQuantity(p: Portion?): Double = p?.suggestedQuantity?.firstOrNull()?.takeIf { it > 0 } ?: 1.0

/** A nutrient's amount per 100 g, matched by shortName or name (0 when missing). */
fun nutrientPer100g(nutrients: List<FoodNutrient>?, aliases: List<String>, unit: String? = null): Double =
    nutrients.orEmpty().firstOrNull { row ->
        val n = row.nutrient
        val named = (n?.shortName ?: "").lowercase() in aliases || (n?.name ?: "").lowercase() in aliases
        named && (unit == null || (n?.unit ?: "").lowercase() == unit)
    }?.amount ?: 0.0

/** N = n × (grams / 100) × quantity */
fun forPortion(per100g: Double, p: Portion?, quantity: Double): Double = per100g * portionGrams(p) / 100 * quantity

fun f1(x: Double) = String.format(Locale.US, "%.1f", x)`;

function ktList(xs: string[]) {
  return `listOf(${xs.map((x) => JSON.stringify(x)).join(", ")})`;
}

function ktPortion(p: SamplePortion) {
  return `Portion(${JSON.stringify(clean(p.name))}, ${d(p.quantity)}, listOf(${d(p.suggestedQuantity)}), Weight(${JSON.stringify(p.unit)}, ${d(p.value)}))`;
}

function ktFood(f: SampleFood, indent: string) {
  const nutrients = f.nutrients
    .map((n) => `${indent}        FoodNutrient(${d(n.amount)}, NutrientInfo(${JSON.stringify(clean(n.name))}, ${JSON.stringify(n.shortName)}, ${JSON.stringify(n.unit)})),`)
    .join("\n");
  const portions = f.portions.map((p) => `${indent}        ${ktPortion(p)},`).join("\n");
  return `Food(
${indent}    name = ${JSON.stringify(clean(f.name))},
${indent}    nutrients = listOf(
${nutrients}
${indent}    ),
${indent}    portions = listOf(
${portions}
${indent}    ),
${indent})`;
}

function ktSnippet(topic: CodeTopic, s: CodeSample): string {
  if (topic === "portion") {
    return `// After GET /v2/products/napi/food/search/result/refCode/{refCode}:
// full nutrients live on results[0].ingredients[i]. Amounts are per 100 g.
${KT_MODELS}

fun main() {
    // ${clean(s.food.name)}, trimmed to the fields used here
    val food = ${ktFood(s.food, "    ")}

    val portion = food.portions?.get(${s.portionIndex})
    val quantity = ${d(s.quantity)} // what the user picked; defaults to defaultQuantity(portion)
    val grams = portionGrams(portion) * quantity
    val m = macros.associate { (key, aliases, unit) -> key to forPortion(nutrientPer100g(food.nutrients, aliases, unit), portion, quantity) }

    println("grams=\${f1(grams)} calories=\${f1(m.getValue("calories"))} protein=\${f1(m.getValue("protein"))} carbs=\${f1(m.getValue("carbs"))} fat=\${f1(m.getValue("fat"))}")
}
`;
  }
  if (topic === "recipe") {
    return `// A recipe's refCode returns several ingredients, each with its own nutrients and portions.
${KT_MODELS}

fun main() {
    val ingredients = listOf(
${s.recipe.ingredients.map((f) => `        ${ktFood(f, "        ")},`).join("\n")}
    )
    val recipePortion = ${ktPortion(s.recipe.portion)}
    val quantity = ${d(s.recipe.quantity)}

    // 1. Weigh each ingredient at its default portion and add up the nutrients.
    var recipeGrams = 0.0
    val totals = mutableMapOf<String, Double>()
    for (ing in ingredients) {
        val p = ing.portions?.firstOrNull()
        val g = portionGrams(p) * defaultQuantity(p)
        recipeGrams += g
        for ((key, aliases, unit) in macros) {
            totals[key] = (totals[key] ?: 0.0) + nutrientPer100g(ing.nutrients, aliases, unit) * g / 100
        }
    }

    // 2. Per-100 g profile for the whole dish, then scale to the dish's portion.
    val grams = portionGrams(recipePortion) * quantity
    fun scaled(key: String) = forPortion((totals[key] ?: 0.0) / recipeGrams * 100, recipePortion, quantity)

    println("recipeGrams=\${f1(recipeGrams)} grams=\${f1(grams)} calories=\${f1(scaled("calories"))} protein=\${f1(scaled("protein"))} carbs=\${f1(scaled("carbs"))} fat=\${f1(scaled("fat"))}")
}
`;
  }
  if (topic === "preview") {
    const p = s.preview.portion;
    return `// Search / scan / extraction results carry nutritionPreview, already scaled to its portion.
// Show it as-is in lists. Don't apply the per-100 g formula to it.
import java.util.Locale

data class Weight(val unit: String?, val value: Double?)
data class PreviewPortion(val name: String?, val quantity: Double?, val weight: Weight?)
data class NutritionPreview(val portion: PreviewPortion?, val calories: Double?, val protein: Double?, val carbs: Double?, val fat: Double?)

val unitToGrams = mapOf("g" to 1.0, "ml" to 1.0, "kg" to 1000.0, "mg" to 0.001, "oz" to 28.3495, "lb" to 453.592)

/** Grams the preview describes. Here portion.quantity IS a count (3 × 52 g eggs). */
fun previewGrams(p: NutritionPreview): Double {
    val portion = p.portion ?: return 0.0
    val w = portion.weight ?: return 0.0
    val value = w.value ?: return 0.0
    if (value <= 0) return 0.0
    val q = portion.quantity ?: 0.0
    return value * (unitToGrams[(w.unit ?: "g").lowercase()] ?: 1.0) * (if (q > 0) q else 1.0)
}

/** Same food, different weight: e.g. the grams a scan predicted, or the user's edit. */
fun rescale(p: NutritionPreview, grams: Double): List<Double> {
    val base = previewGrams(p)
    val f = if (base > 0) grams / base else 0.0
    return listOf(p.calories, p.protein, p.carbs, p.fat).map { (it ?: 0.0) * f }
}

fun f1(x: Double) = String.format(Locale.US, "%.1f", x)

fun main() {
    // ${clean(s.preview.name)}
    val preview = NutritionPreview(
        portion = PreviewPortion(${JSON.stringify(clean(p.name))}, ${d(p.quantity)}, Weight(${JSON.stringify(p.unit)}, ${d(p.value)})),
        calories = ${d(s.preview.calories)}, protein = ${d(s.preview.protein)}, carbs = ${d(s.preview.carbs)}, fat = ${d(s.preview.fat)},
    )
    val grams = ${d(s.preview.grams)}
    val (calories, protein, carbs, fat) = rescale(preview, grams)

    println("previewGrams=\${f1(previewGrams(preview))} grams=\${f1(grams)} calories=\${f1(calories)} protein=\${f1(protein)} carbs=\${f1(carbs)} fat=\${f1(fat)}")
}
`;
  }
  return `// Each item is already scaled to what was eaten (preview as-is, or the portion math).
import java.util.Locale
import kotlin.math.roundToInt

data class Macros(val calories: Double = 0.0, val protein: Double = 0.0, val carbs: Double = 0.0, val fat: Double = 0.0)

/** Meal = sum of its items. A day = sum of its meals (never re-add a total you were sent). */
fun sum(items: List<Macros>) = items.fold(Macros()) { t, i -> Macros(t.calories + i.calories, t.protein + i.protein, t.carbs + i.carbs, t.fat + i.fat) }

/** % of calories from protein / carbs / fat (4, 4 and 9 kcal per gram). */
fun macroSplit(t: Macros): List<Int> {
    val parts = listOf(t.protein * 4, t.carbs * 4, t.fat * 9)
    val whole = parts.sum().takeIf { it > 0 } ?: 1.0
    return parts.map { (it / whole * 100).roundToInt() }
}

fun f1(x: Double) = String.format(Locale.US, "%.1f", x)

fun main() {
    val meal = listOf(
${s.meal
  .map(roundItem)
  .map((m) => `        Macros(${d(m.calories)}, ${d(m.protein)}, ${d(m.carbs)}, ${d(m.fat)}), // ${m.name}`)
  .join("\n")}
    )
    val total = sum(meal)
    println("calories=\${f1(total.calories)} protein=\${f1(total.protein)} carbs=\${f1(total.carbs)} fat=\${f1(total.fat)} split=\${macroSplit(total).joinToString("/")}")
}
`;
}

// ---------- Sample data ----------

const nut = (name: string, shortName: string, unit: string, amount: number): SampleNutrient => ({ name, shortName, unit, amount });
const por = (name: string, value: number, suggestedQuantity = 1): SamplePortion => ({ name, quantity: 1, suggestedQuantity, unit: "g", value });
const macrosOf = (kcal: number, p: number, c: number, f: number) => [nut("Energy", "calories", "KCAL", kcal), nut("Protein", "protein", "G", p), nut("Carbohydrate", "carbs", "G", c), nut("Total fat", "fat", "G", f)];

export const EXAMPLE_FOOD: SampleFood = { name: "Banana, raw", nutrients: macrosOf(89, 1.09, 22.84, 0.33), portions: [por("medium", 118), por("cup, sliced", 150), por("g", 1, 100)] };

const EXAMPLE_RECIPE: CodeSample["recipe"] = {
  name: "Banana peanut butter oatmeal",
  ingredients: [
    { name: "Rolled oats", nutrients: macrosOf(379, 13.15, 67.7, 6.52), portions: [por("cup", 80, 0.5)] },
    { name: "Milk, 2%", nutrients: macrosOf(50, 3.3, 4.8, 1.98), portions: [por("cup", 244)] },
    { name: "Banana, raw", nutrients: macrosOf(89, 1.09, 22.84, 0.33), portions: [por("medium", 118, 0.5)] },
    { name: "Peanut butter", nutrients: macrosOf(588, 25.09, 19.56, 50.39), portions: [por("tbsp", 16)] },
  ],
  portion: por("bowl", 359),
  quantity: 1,
};

const EXAMPLE_PREVIEW: CodeSample["preview"] = { name: "Eggs, scrambled (scan result)", portion: { name: "large egg", quantity: 3, unit: "g", value: 52 }, calories: 233, protein: 15.6, carbs: 2.5, fat: 17.7, grams: 104 };

const EXAMPLE_MEAL_EXTRAS: SampleMacros[] = [
  { name: "Greek yogurt, plain, 170 g", calories: 100, protein: 17.3, carbs: 6.1, fat: 0.7 },
  { name: "Granola, 30 g", calories: 140, protein: 3.2, carbs: 19.8, fat: 5.9 },
];

/** The rows the examples use (calories in kcal, protein, carbs, fat), from a full nutrient list. */
export function sampleNutrients(nutrients: Pick<PassioFoodNutrient, "amount" | "nutrient">[] | undefined): SampleNutrient[] {
  const out: SampleNutrient[] = [];
  for (const [key, aliases] of Object.entries(MACRO_ALIASES)) {
    const hit = (nutrients ?? []).find((r) => {
      const n = r.nutrient ?? {};
      const named = aliases.includes((n.shortName ?? "").toLowerCase()) || aliases.includes((n.name ?? "").toLowerCase());
      return named && (key !== "calories" || (n.unit ?? "").toLowerCase() === "kcal");
    });
    if (hit) out.push(nut(hit.nutrient?.name || key, hit.nutrient?.shortName || key, hit.nutrient?.unit || (key === "calories" ? "KCAL" : "G"), Number(hit.amount) || 0));
  }
  return out;
}

export function samplePortion(p: PassioMeasuredPortion | undefined): SamplePortion {
  const value = Number(p?.weight?.value);
  const hasWeight = value > 0;
  return {
    name: p?.name || "100 g",
    quantity: Number(p?.quantity) || 1,
    suggestedQuantity: Number(p?.suggestedQuantity?.[0]) || 1,
    unit: hasWeight ? p?.weight?.unit || "g" : "g",
    value: hasWeight ? value : 100,
  };
}

export interface SampleInput {
  food?: SampleFood;
  portionIndex?: number;
  quantity?: number;
  recipe?: CodeSample["recipe"];
  preview?: Omit<CodeSample["preview"], "grams">;
}

/** Live data where we have it, example data for the rest. */
export function buildSample(input: SampleInput = {}): CodeSample {
  const food = input.food && input.food.nutrients.length && input.food.portions.length ? input.food : EXAMPLE_FOOD;
  const portionIndex = food === input.food ? Math.min(input.portionIndex ?? 0, food.portions.length - 1) : 0;
  const portion = food.portions[portionIndex];
  const quantity = food === input.food && input.quantity && input.quantity > 0 ? input.quantity : portion.suggestedQuantity || 1;
  const grams = portionWeightG(toApiPortion(portion)) * quantity;
  const m = macrosFor(nutrientProfilePer100g([{ nutrients: toApiNutrients(food) }]), grams);
  const eaten: SampleMacros = { name: `${clean(food.name)}, ${+grams.toFixed(1)} g`, calories: m.calories, protein: m.protein, carbs: m.carbs, fat: m.fat };
  const previewBase = input.preview?.portion.value ? input.preview : undefined;
  const preview = previewBase
    ? { ...previewBase, grams: +(food === input.food ? grams : previewGrams({ portion: toApiPortion(previewBase.portion) }) * 1.5).toFixed(1) }
    : EXAMPLE_PREVIEW;
  return {
    food,
    portionIndex,
    quantity,
    recipe: input.recipe && input.recipe.ingredients.length > 1 ? input.recipe : EXAMPLE_RECIPE,
    preview,
    meal: [eaten, ...EXAMPLE_MEAL_EXTRAS],
  };
}

export function nutritionCode(topic: CodeTopic, lang: CodeLang, sample: CodeSample): string {
  if (lang === "python") return pySnippet(topic, sample);
  if (lang === "swift") return swiftSnippet(topic, sample);
  if (lang === "kotlin") return ktSnippet(topic, sample);
  return tsSnippet(topic, sample);
}
